import type { AnswerValue, Progress, PublicPuzzle } from '@/lib/api'
import { ChoicePuzzle } from '@/game/puzzles/ChoicePuzzle'
import { TextPuzzle } from '@/game/puzzles/TextPuzzle'
import { NumberPuzzle } from '@/game/puzzles/NumberPuzzle'
import { OrderingPuzzle } from '@/game/puzzles/OrderingPuzzle'
import { MatchingPuzzle } from '@/game/puzzles/MatchingPuzzle'
import { CategorizationPuzzle } from '@/game/puzzles/CategorizationPuzzle'
import { HotspotPuzzle } from '@/game/puzzles/HotspotPuzzle'
import { MemoryPuzzle } from '@/game/puzzles/MemoryPuzzle'
import { ActionPuzzle } from '@/game/puzzles/ActionPuzzle'
import { PhotoPuzzle } from '@/game/puzzles/PhotoPuzzle'

export type PuzzleRendererProps = {
  puzzle: PublicPuzzle
  progress: Progress | null
  /** Server time when this state was produced; used to align client countdowns. */
  serverTime: string
  onSubmit: (value: AnswerValue) => void | Promise<void>
  /** Non-answer interaction (memory reveal start, timed action start). */
  onAction: (action: string) => void | Promise<void>
  /** Photo puzzles: upload a file; the server decides completion. */
  onUploadPhoto: (file: File, onProgress?: (fraction: number) => void) => void | Promise<void>
  /** Ask for the authoritative state again (e.g. when a server-timed phase ends). */
  onResync: () => void | Promise<void>
  disabled: boolean
}

/** Selects the renderer for a puzzle by its type. Content is data; no per-puzzle components. */
export function PuzzleRenderer(props: PuzzleRendererProps) {
  switch (props.puzzle.type) {
    case 'choice':
      return <ChoicePuzzle {...props} />
    case 'text':
      return <TextPuzzle {...props} />
    case 'number':
      return <NumberPuzzle {...props} />
    case 'ordering':
      return <OrderingPuzzle {...props} />
    case 'matching':
      return <MatchingPuzzle {...props} />
    case 'categorization':
      return <CategorizationPuzzle {...props} />
    case 'hotspot':
      return <HotspotPuzzle {...props} />
    case 'memory':
      return <MemoryPuzzle {...props} />
    case 'action':
      return <ActionPuzzle {...props} />
    case 'photo':
      return <PhotoPuzzle {...props} />
    default:
      return (
        <p className="status-err" role="alert">
          This puzzle type ({props.puzzle.type}) is not supported by this client yet.
        </p>
      )
  }
}
